/**
 * Readiness condition for streaming suspension. Always a single shape:
 * an explicit stable `key` (used as the promise-cache key per object) and
 * an `isMet` predicate. Build key-presence conditions with
 * {@link requiredKeys}.
 */
export interface StreamingCondition {
  /** Stable cache key — same object + same key share one suspension promise. */
  key: string;
  isMet: (value: unknown, isStreaming: boolean) => boolean;
}

/** Type guard for plain objects (not arrays, not null). */
const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Condition met when every listed key holds a non-null value.
 *
 * @remarks
 * Values are considered "incomplete" if the key is missing, `undefined`,
 * `null`, or — only while streaming — an empty string `''` (partial tokens
 * often surface as empty strings before the full value arrives; after
 * streaming ends, empty strings are valid final values).
 */
export function requiredKeys(keys: readonly string[]): StreamingCondition {
  return {
    key: `keys:${[...keys].sort().join(',')}`,
    isMet: (value, isStreaming) =>
      isPlainObject(value) &&
      keys.every((key) => {
        const val = value[key];
        if (val === undefined || val === null) {
          return false;
        }
        return !(isStreaming && val === '');
      }),
  };
}
